import type * as THREE from 'three';
import type { Wall } from './station.ts';

/**
 * Hides whichever walls stand between the camera and the room, so the diorama
 * can be spun a full turn without ever showing its outside.
 *
 * For a wall with inward-facing normal n, the camera is outside it - and so the
 * wall is in the way - when
 *
 *     dot(n, cameraPosition - wallCenter) < 0
 *
 * At a 45 degree azimuth exactly two of the four satisfy that, which is what
 * produces the open-corner diorama at every quarter turn.
 */

/**
 * Dead band around the decision boundary. Without it, a camera parked at a
 * cardinal azimuth sits where the dot product is ~0 and the wall flickers
 * between states on every frame of damping.
 */
const HYSTERESIS = 0.75;

export function createWallCuller(walls: Wall[], camera: THREE.Camera) {
  // Start from the geometric answer rather than "all visible", or the first
  // frame shows the outside of the room.
  const hidden = walls.map(() => false);

  return function update(): void {
    for (let i = 0; i < walls.length; i++) {
      const wall = walls[i];
      const side = wall.normal.x * (camera.position.x - wall.center.x) +
        wall.normal.y * (camera.position.y - wall.center.y) +
        wall.normal.z * (camera.position.z - wall.center.z);

      // Only cross a threshold that is clear of the boundary; in between, keep
      // whatever state this wall already had.
      if (side < -HYSTERESIS) hidden[i] = true;
      else if (side > HYSTERESIS) hidden[i] = false;

      wall.mesh.visible = !hidden[i];
    }
  };
}
